import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  appacApi,
  appacStudioBaseUrl,
  type AppacCacheEntry,
  type AppacCaseSpec,
  type AppacEvent,
  type AppacFrame,
  type AppacHealth,
  type AppacJob,
  type AppacSweep,
} from "./api";
import { humanBytes, parseAxisValues } from "./model";
import {
  listAircraftDesigns,
  type AircraftDesignSummary,
} from "../../api/aircraft";
import {
  SectionCampaignWorkbench,
  type SectionCampaignCoverage,
  type SectionCampaignRequest,
  type SectionCampaignResult,
} from "./SectionCampaignWorkbench";
import {
  DEFAULT_WING,
  QUICK_LIFTING_LINE_MAX_STATIONS,
  addWingStation,
  buildWingPreview,
  removeWingStation,
  updateWingStation,
  validateWing,
  wingMetrics,
  type WingDefinition,
  type WingStation,
} from "./wingModel";
import { WakeScene } from "./WakeScene";
import { isUuidLike } from "../thread-v4/viewSpecs";
import "./appac-wake.css";

const DEFAULT_SPEC: AppacCaseSpec = {
  case: "custom-wing",
  alpha: 4.2,
  beta: 0,
  backend: "lu",
  max_iter: 1000,
  conv_tol: 1e-5,
  snapshot_interval: 5,
};

type Tab = "design" | "runs" | "sweeps" | "cache";
type WorkspaceMode = "section-campaign" | "appac-wake";

export interface AppacWakeWorkbenchProps {
  embedded?: boolean;
  projectId?: string;
  projectName?: string;
  branch?: string;
  /** Stable logical identity for the aircraft design, when supplied by Thread. */
  aircraftModelId?: string;
  /** Project-owned VSP design selected by the Thread route. */
  aircraftDesignId?: string;
  onAircraftDesignChange?: (designId: string) => void;
  sectionCoverage?: SectionCampaignCoverage;
  onQueueSectionCampaign?: (request: SectionCampaignRequest) => Promise<SectionCampaignResult | void> | SectionCampaignResult | void;
}

function cloneDefaultWing(): WingDefinition {
  return JSON.parse(JSON.stringify(DEFAULT_WING)) as WingDefinition;
}

function shortTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .format(new Date(value));
}

function statusLabel(job: AppacJob | null): string {
  if (!job) return "idle";
  if (job.cached) return "cache hit";
  return job.status;
}

function caseLabel(value: AppacCaseSpec["case"]): string {
  if (value === "custom-wing") return "Parametric wing";
  if (value === "sweptwing") return "APPAC swept-wing fixture";
  return "APPAC BWB fixture";
}

function numberValue(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function loadDraft(storageKey: string): WingDefinition {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return cloneDefaultWing();
    const parsed = JSON.parse(raw) as WingDefinition;
    return validateWing(parsed).length ? cloneDefaultWing() : parsed;
  } catch {
    return cloneDefaultWing();
  }
}

function randomUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

function loadOrCreateAircraftModelId(storageKey: string): string {
  const key = `${storageKey}:aircraft-model-id`;
  try {
    const existing = window.localStorage.getItem(key);
    if (existing && isUuidLike(existing)) return existing;
    const created = randomUuid();
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return randomUuid();
  }
}

function geometryPreset(kind: "straight" | "swept" | "cranked"): WingDefinition {
  if (kind === "cranked") return cloneDefaultWing();
  if (kind === "straight") {
    return {
      ...cloneDefaultWing(),
      name: "Straight tapered wing",
      stations: [
        { id: "root", y: 0, x_le: 0, z_le: 0, chord: 2.2, twist_deg: 1, airfoil: "NACA 2412" },
        { id: "tip", y: 5.6, x_le: 0, z_le: 0.25, chord: 0.9, twist_deg: -2, airfoil: "NACA 0010" },
      ],
    };
  }
  return {
    ...cloneDefaultWing(),
    name: "Swept tapered wing",
    stations: [
      { id: "root", y: 0, x_le: 0, z_le: 0, chord: 2.4, twist_deg: 1.5, airfoil: "NACA 2412" },
      { id: "tip", y: 6, x_le: 1.65, z_le: 0.4, chord: 0.72, twist_deg: -2.5, airfoil: "NACA 0010" },
    ],
  };
}

export default function AppacWakeWorkbench({
  embedded = false,
  projectId,
  projectName = "APPAC",
  branch = "main",
  aircraftModelId: suppliedAircraftModelId,
  aircraftDesignId: suppliedAircraftDesignId,
  onAircraftDesignChange,
  sectionCoverage,
  onQueueSectionCampaign,
}: AppacWakeWorkbenchProps) {
  const baseStorageKey = `thread:aero-wing-draft:${projectId ?? "standalone"}:${branch}`;
  const [aircraftModelId, setAircraftModelId] = useState(
    () => suppliedAircraftModelId ?? loadOrCreateAircraftModelId(baseStorageKey),
  );
  const [aircraftDesigns, setAircraftDesigns] = useState<AircraftDesignSummary[]>([]);
  const [activeAircraftDesignId, setActiveAircraftDesignId] = useState<string | null>(
    suppliedAircraftDesignId ?? null,
  );
  const [aircraftIdentityPending, setAircraftIdentityPending] = useState(
    () => !suppliedAircraftModelId && Boolean(projectId && isUuidLike(projectId)),
  );
  const [aircraftIdentityError, setAircraftIdentityError] = useState<string | null>(null);
  const storageKey = `${baseStorageKey}:aircraft:${activeAircraftDesignId ?? suppliedAircraftDesignId ?? "standalone"}`;
  const [health, setHealth] = useState<AppacHealth | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [spec, setSpec] = useState<AppacCaseSpec>(DEFAULT_SPEC);
  const [wing, setWing] = useState<WingDefinition>(() => loadDraft(storageKey));
  const [selectedStationId, setSelectedStationId] = useState(() => wing.stations[1]?.id ?? wing.stations[0]!.id);
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>("section-campaign");
  const [tab, setTab] = useState<Tab>("design");
  const [jobs, setJobs] = useState<AppacJob[]>([]);
  const [job, setJob] = useState<AppacJob | null>(null);
  const [events, setEvents] = useState<AppacEvent[]>([]);
  const [frame, setFrame] = useState<AppacFrame | null>(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [followLive, setFollowLive] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [cache, setCache] = useState<AppacCacheEntry[]>([]);
  const [sweep, setSweep] = useState<AppacSweep | null>(null);
  const [alphaAxis, setAlphaAxis] = useState("0:8:2");
  const [betaAxis, setBetaAxis] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const lastEventRef = useRef(0);
  const lastFrameCountRef = useRef(0);

  useEffect(() => {
    const next = loadDraft(storageKey);
    setWing(next);
    setSelectedStationId(next.stations[1]?.id ?? next.stations[0]!.id);
  }, [storageKey]);

  useEffect(() => {
    if (suppliedAircraftModelId) {
      setAircraftModelId(suppliedAircraftModelId);
      setAircraftIdentityError(null);
      setAircraftIdentityPending(false);
      return;
    }
    if (!projectId || !isUuidLike(projectId)) {
      setAircraftDesigns([]);
      setActiveAircraftDesignId(null);
      setAircraftModelId(loadOrCreateAircraftModelId(baseStorageKey));
      setAircraftIdentityError(null);
      setAircraftIdentityPending(false);
      return;
    }
    setAircraftIdentityError(null);
    setAircraftIdentityPending(true);
    let active = true;
    void listAircraftDesigns(projectId, branch).then((response) => {
      if (!active) return;
      const designs = [...response.designs].sort((left, right) =>
        left.design_id.localeCompare(right.design_id),
      );
      setAircraftDesigns(designs);
      const selected = designs.find(
        (design) => design.design_id === suppliedAircraftDesignId,
      ) ?? designs[0];
      if (!selected && isUuidLike(response.default_section_model_logical_id)) {
        setActiveAircraftDesignId(null);
        setAircraftModelId(response.default_section_model_logical_id!);
        setAircraftIdentityError(null);
        setAircraftIdentityPending(false);
        return;
      }
      if (!selected || !isUuidLike(selected.section_model_logical_id)) {
        setActiveAircraftDesignId(null);
        setAircraftIdentityError(
          "This Thread project has no usable aircraft design. Create or restore one in the Aircraft workbench before running a persisted section campaign.",
        );
        setAircraftIdentityPending(false);
        return;
      }
      setActiveAircraftDesignId(selected.design_id);
      setAircraftModelId(selected.section_model_logical_id!);
      if (selected.design_id !== suppliedAircraftDesignId) {
        onAircraftDesignChange?.(selected.design_id);
      }
      setAircraftIdentityPending(false);
    }).catch(() => {
      if (!active) return;
      setAircraftDesigns([]);
      setActiveAircraftDesignId(null);
      setAircraftIdentityError(
        "Thread could not resolve this project's aircraft design. Reconnect, then reload before running a persisted section campaign.",
      );
      setAircraftIdentityPending(false);
    });
    return () => { active = false; };
  }, [baseStorageKey, branch, onAircraftDesignChange, projectId, suppliedAircraftDesignId, suppliedAircraftModelId]);

  const selectAircraftDesign = (designId: string) => {
    const selected = aircraftDesigns.find((design) => design.design_id === designId);
    if (!selected || !isUuidLike(selected.section_model_logical_id)) return;
    setActiveAircraftDesignId(selected.design_id);
    setAircraftModelId(selected.section_model_logical_id!);
    onAircraftDesignChange?.(selected.design_id);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(wing));
    } catch {
      // Draft persistence is a convenience; solver operation remains usable without it.
    }
  }, [storageKey, wing]);

  const wingErrors = useMemo(() => validateWing(wing), [wing]);
  const metrics = useMemo(() => wingErrors.length ? null : wingMetrics(wing), [wing, wingErrors]);
  const previewBody = useMemo(() => wingErrors.length ? null : buildWingPreview(wing), [wing, wingErrors]);
  const selectedStation = wing.stations.find((station) => station.id === selectedStationId) ?? wing.stations[0]!;
  const activeSpec = useMemo<AppacCaseSpec>(() => spec.case === "custom-wing"
    ? { ...spec, beta: 0, wing }
    : { ...spec, wing: undefined }, [spec, wing]);

  const refreshLists = useCallback(async () => {
    const [jobData, cacheData] = await Promise.all([appacApi.jobs(), appacApi.cache()]);
    setJobs(jobData.jobs);
    setCache(cacheData.entries);
  }, []);

  const connect = useCallback(async () => {
    try {
      const next = await appacApi.health();
      setHealth(next);
      setConnectionError(null);
      await refreshLists();
    } catch (error) {
      setHealth(null);
      setConnectionError(error instanceof Error ? error.message : "V100 runner unavailable");
    }
  }, [refreshLists]);

  // The section campaign is deliberately useful without the optional CUDA
  // fixture runner. Do not probe a browser-direct V100 until that mode opens.
  useEffect(() => {
    if (workspaceMode === "appac-wake") void connect();
  }, [connect, workspaceMode]);

  const loadFrame = useCallback(async (target: number | "latest") => {
    if (!job) return;
    try {
      const next = await appacApi.frame(job.id, target);
      setFrame(next);
      setFrameIndex(next.index);
    } catch (error) {
      if (target !== "latest") setActionError(error instanceof Error ? error.message : "frame unavailable");
    }
  }, [job]);

  useEffect(() => {
    if (!job || job.status === "done" || job.status === "error") return;
    const poll = window.setInterval(() => {
      void Promise.all([appacApi.job(job.id), appacApi.events(job.id, lastEventRef.current)])
        .then(async ([nextJob, nextEvents]) => {
          setJob(nextJob);
          setJobs((current) => current.map((item) => item.id === nextJob.id ? nextJob : item));
          if (nextEvents.events.length) {
            lastEventRef.current = nextEvents.events[nextEvents.events.length - 1]!.seq;
            setEvents((current) => [...current, ...nextEvents.events]);
          }
          if (followLive && nextJob.frame_count > lastFrameCountRef.current) {
            lastFrameCountRef.current = nextJob.frame_count;
            await loadFrame("latest");
          }
          if (nextJob.status === "done" || nextJob.status === "error") await refreshLists();
        })
        .catch((error: unknown) => setActionError(error instanceof Error ? error.message : "poll failed"));
    }, 850);
    return () => window.clearInterval(poll);
  }, [followLive, job, loadFrame, refreshLists]);

  useEffect(() => {
    if (!playing || !job || job.frame_count < 2) return;
    const timer = window.setInterval(() => {
      const next = (frameIndex + 1) % job.frame_count;
      setFollowLive(false);
      void loadFrame(next);
    }, 650);
    return () => window.clearInterval(timer);
  }, [frameIndex, job, loadFrame, playing]);

  useEffect(() => {
    if (!sweep || (sweep.states.queued === 0 && sweep.states.running === 0)) return;
    const timer = window.setInterval(() => {
      void appacApi.sweep(sweep.id).then(setSweep).catch((error: unknown) => {
        setActionError(error instanceof Error ? error.message : "sweep poll failed");
      });
    }, 1100);
    return () => window.clearInterval(timer);
  }, [sweep]);

  const selectJob = useCallback(async (nextJob: AppacJob) => {
    setJob(nextJob);
    setEvents([]);
    setFrame(null);
    setPlaying(false);
    setFollowLive(true);
    setActionError(null);
    lastEventRef.current = 0;
    lastFrameCountRef.current = 0;
    const eventData = await appacApi.events(nextJob.id, 0);
    setEvents(eventData.events);
    lastEventRef.current = eventData.events[eventData.events.length - 1]?.seq ?? 0;
    if (nextJob.frame_count > 0) {
      lastFrameCountRef.current = nextJob.frame_count;
      const nextFrame = await appacApi.frame(nextJob.id, "latest");
      setFrame(nextFrame);
      setFrameIndex(nextFrame.index);
    }
  }, []);

  const run = async (force = false, override: AppacCaseSpec = activeSpec) => {
    if (override.case === "custom-wing" && wingErrors.length) {
      setActionError(wingErrors[0]!);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const next = await appacApi.submit(override, force);
      setJobs((current) => [next, ...current.filter((item) => item.id !== next.id)]);
      setTab("runs");
      if (next.status === "done" || next.frame_count > 0) {
        await selectJob(next);
      } else {
        // A newly queued solve has no events or frames to hydrate yet. Selecting
        // it immediately lets the normal poller own the live transition and
        // avoids turning a transient tunnel read into a false submit failure.
        setJob(next);
        setEvents([]);
        setFrame(null);
        setPlaying(false);
        setFollowLive(true);
        lastEventRef.current = 0;
        lastFrameCountRef.current = 0;
      }
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "submission failed");
    } finally {
      setBusy(false);
    }
  };

  const runSweep = async () => {
    if (activeSpec.case === "custom-wing" && wingErrors.length) {
      setActionError(wingErrors[0]!);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const axes: Record<string, number[]> = {};
      const alpha = parseAxisValues(alphaAxis);
      const beta = parseAxisValues(betaAxis);
      if (alpha.length) axes.alpha = alpha;
      if (beta.length) axes.beta = beta;
      if (!Object.keys(axes).length) throw new Error("enter at least one sweep axis");
      if (activeSpec.case === "custom-wing" && beta.some((value) => Math.abs(value) > 1e-12)) {
        throw new Error("half-wing symmetry currently requires β = 0");
      }
      setSweep(await appacApi.submitSweep(activeSpec, axes));
      setTab("sweeps");
      await refreshLists();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "sweep submission failed");
    } finally {
      setBusy(false);
    }
  };

  const changeStation = (patch: Partial<Omit<WingStation, "id">>) => {
    setWing((current) => updateWingStation(current, selectedStation.id, patch));
  };

  const addStation = () => {
    if (wing.stations.length >= QUICK_LIFTING_LINE_MAX_STATIONS) return;
    const next = addWingStation(wing, selectedStation.id);
    const added = next.stations.find((station) => !wing.stations.some((old) => old.id === station.id));
    setWing(next);
    if (added) setSelectedStationId(added.id);
  };

  const deleteStation = () => {
    const index = wing.stations.findIndex((station) => station.id === selectedStation.id);
    const next = removeWingStation(wing, selectedStation.id);
    setWing(next);
    setSelectedStationId(next.stations[Math.min(index, next.stations.length - 1)]!.id);
  };

  const choosePreset = (kind: "straight" | "swept" | "cranked") => {
    const next = geometryPreset(kind);
    setWing(next);
    setSelectedStationId(next.stations[1]?.id ?? next.stations[0]!.id);
    setFrame(null);
    setJob(null);
    setTab("design");
  };

  const progress = job ? Math.min(1, job.latest_iteration / job.spec.max_iter) : 0;
  const completedSweep = sweep?.states.done ?? 0;
  const totalSweep = sweep?.jobs.length ?? 0;
  const cacheBytes = useMemo(() => cache.reduce((sum, entry) => sum + entry.size_bytes, 0), [cache]);
  const rootClass = `appac-studio has-workspace-switch${embedded ? " is-embedded" : ""}`;

  return (
    <main className={rootClass} data-testid={embedded ? "aero-design-surface" : "appac-wake-workbench"}>
      {!embedded ? (
        <header className="appac-studio-header">
          <div className="appac-wordmark" aria-label="Thread Aero Design">
            <span className="appac-thread-mark">T</span>
            <div><strong>Thread</strong><small>{workspaceMode === "section-campaign" ? "Aero design · section campaign" : "Aero design · APPAC wake"}</small></div>
          </div>
          <div className="appac-revision"><span>{workspaceMode === "section-campaign" ? "MODEL" : "APPAC"}</span><code>{workspaceMode === "section-campaign" ? "geometry → 2D" : health?.appac_commit.slice(0, 8) ?? "f2a7568"}</code></div>
          {workspaceMode === "section-campaign" ? (
            <div className="appac-connection is-online">
              <i />
              <div><strong>Campaign planner ready</strong><small>solver adapter seam</small></div>
              <button type="button" onClick={() => setWorkspaceMode("appac-wake")}>Open wake</button>
            </div>
          ) : (
            <div className={`appac-connection ${health?.cuda.available ? "is-online" : ""}`}>
              <i />
              <div><strong>{health?.cuda.device ?? "V100 disconnected"}</strong><small>{appacStudioBaseUrl()}</small></div>
              <button type="button" onClick={() => void connect()}>Reconnect</button>
            </div>
          )}
        </header>
      ) : null}

      <nav className="appac-workspace-switch" aria-label="Aero workbench mode">
        <div>
          <button type="button" className={workspaceMode === "section-campaign" ? "is-active" : ""} onClick={() => setWorkspaceMode("section-campaign")}>
            <strong>Section campaign</strong>
          </button>
          <button type="button" className={workspaceMode === "appac-wake" ? "is-active" : ""} onClick={() => setWorkspaceMode("appac-wake")}>
            <strong>Wake (advanced)</strong>
          </button>
        </div>
        {workspaceMode === "section-campaign" && aircraftDesigns.length ? (
          <label className="appac-aircraft-identity">
            <span>Aircraft</span>
            <select
              aria-label="Aircraft design identity"
              value={activeAircraftDesignId ?? ""}
              onChange={(event) => selectAircraftDesign(event.target.value)}
            >
              {aircraftDesigns.map((design) => (
                <option key={design.design_id} value={design.design_id}>
                  {design.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </nav>

      {workspaceMode === "section-campaign" && aircraftIdentityPending ? (
        <div className="section-aircraft-identity-loading" aria-busy="true">
          Resolving the project-owned aircraft identity…
        </div>
      ) : workspaceMode === "section-campaign" && aircraftIdentityError ? (
        <div className="section-aircraft-identity-loading is-error" role="alert">
          <strong>Aircraft identity required</strong>
          <span>{aircraftIdentityError}</span>
        </div>
      ) : workspaceMode === "section-campaign" ? (
        <SectionCampaignWorkbench
          wing={wing}
          selectedStationId={selectedStation.id}
          previewBody={previewBody}
          projectId={projectId}
          aircraftModelId={aircraftModelId}
          aircraftDesignId={activeAircraftDesignId ?? undefined}
          projectName={projectName}
          branch={branch}
          coverage={sectionCoverage}
          onWingChange={setWing}
          onSelectStation={setSelectedStationId}
          onAddStation={addStation}
          onDeleteStation={deleteStation}
          onQueueCampaign={onQueueSectionCampaign}
        />
      ) : (
        <div className="appac-studio-body">
        <aside className="appac-rail" aria-label="Aero workspace sections">
          {(["design", "runs", "sweeps", "cache"] as Tab[]).map((item) => (
            <button type="button" key={item} className={tab === item ? "is-active" : ""} onClick={() => setTab(item)}>
              <span>{item === "design" ? "G" : item === "runs" ? "R" : item === "sweeps" ? "S" : "C"}</span>
              {item}
              {item === "cache" && cache.length > 0 ? <b>{cache.length}</b> : null}
            </button>
          ))}
        </aside>

        <section className="appac-sidebar">
          {tab === "design" ? (
            <>
              <div className="appac-panel-heading"><span>Geometry tree</span><small>{wing.stations.length} sections</small></div>
              <div className="appac-geometry-tree">
                <button type="button" className="appac-tree-root is-selected"><i>W</i><span><strong>{wing.name}</strong><small>Symmetric lifting surface</small></span></button>
                <div className="appac-station-list">
                  {wing.stations.map((station, index) => (
                    <button type="button" key={station.id} className={selectedStation.id === station.id ? "is-selected" : ""} onClick={() => setSelectedStationId(station.id)}>
                      <i>{index === 0 ? "R" : index === wing.stations.length - 1 ? "T" : index}</i>
                      <span><strong>{station.id}</strong><small>η {(station.y / (metrics?.semi_span || 1)).toFixed(3)} · c {station.chord.toFixed(2)}</small></span>
                    </button>
                  ))}
                </div>
                <button type="button" className="appac-add-station" disabled={wing.stations.length >= QUICK_LIFTING_LINE_MAX_STATIONS} onClick={addStation}>＋ Add section after {selectedStation.id}</button>
              </div>
              <div className="appac-preset-block">
                <div className="appac-panel-heading"><span>Start shape</span><small>presets</small></div>
                <div><button type="button" onClick={() => choosePreset("straight")}>Straight</button><button type="button" onClick={() => choosePreset("swept")}>Swept</button><button type="button" onClick={() => choosePreset("cranked")}>Cranked</button></div>
              </div>
              {metrics ? (
                <dl className="appac-wing-metrics">
                  <div><dt>Span</dt><dd>{metrics.span.toFixed(2)} m</dd></div>
                  <div><dt>Area</dt><dd>{metrics.area.toFixed(2)} m²</dd></div>
                  <div><dt>AR</dt><dd>{metrics.aspect_ratio.toFixed(2)}</dd></div>
                  <div><dt>Panels</dt><dd>{wing.chord_panels * 2 * wing.span_panels_per_segment * (wing.stations.length - 1)}</dd></div>
                </dl>
              ) : null}
              <p className="appac-draft-note">Draft scoped to {projectName} / {branch} in this browser.</p>
            </>
          ) : null}

          {tab === "runs" ? (
            <>
              <div className="appac-panel-heading"><span>Run history</span><small>{jobs.length} session</small></div>
              <div className="appac-run-list">
                {jobs.map((item) => (
                  <button type="button" key={item.id} onClick={() => void selectJob(item)} className={job?.id === item.id ? "is-selected" : ""}>
                    <i className={`status-${item.status}`} />
                    <span><strong>α {item.spec.alpha}° · β {item.spec.beta}°</strong><small>{item.spec.case} · {item.id}</small></span>
                    <em>{item.cached ? "cached" : item.status}</em>
                  </button>
                ))}
                {!jobs.length ? <p className="appac-list-empty">No runs in this V100 session.</p> : null}
              </div>
            </>
          ) : null}

          {tab === "sweeps" ? (
            <>
              <div className="appac-panel-heading"><span>Sweep queue</span><small>{completedSweep}/{totalSweep}</small></div>
              <div className="appac-sweep-list">
                {sweep?.jobs.map((item) => (
                  <button type="button" key={item.id} onClick={() => void selectJob(item)}>
                    <i className={`status-${item.status}`} />
                    <span><strong>α {item.spec.alpha.toFixed(1)}° · β {item.spec.beta.toFixed(1)}°</strong><small>{item.latest_residual == null ? item.status : `rms ${item.latest_residual.toExponential(2)}`}</small></span>
                  </button>
                )) ?? <p className="appac-list-empty">Define a sweep in the solver panel.</p>}
              </div>
            </>
          ) : null}

          {tab === "cache" ? (
            <>
              <div className="appac-panel-heading"><span>Result cache</span><small>{humanBytes(cacheBytes)}</small></div>
              <div className="appac-cache-list">
                {cache.map((entry) => (
                  <button type="button" key={entry.cache_key} onClick={() => void run(false, entry.spec)}>
                    <code>{entry.cache_key}</code>
                    <span>α {entry.spec.alpha}° · {entry.spec.case}</span>
                    <small>{entry.frame_count} frames · {humanBytes(entry.size_bytes)}</small>
                  </button>
                ))}
                {!cache.length ? <p className="appac-list-empty">Completed solves appear here.</p> : null}
              </div>
            </>
          ) : null}
        </section>

        <section className="appac-stage">
          <div className="appac-stage-toolbar">
            <div><span>GEOMETRY / WAKE</span><strong>{frame && job ? `${caseLabel(job.spec.case)} · α ${job.spec.alpha}°` : wing.name}</strong></div>
            <div className="appac-stage-actions">
              <button type="button" className={`appac-v100-chip ${health?.cuda.available ? "is-online" : ""}`} onClick={() => void connect()}><i />{health?.cuda.available ? "V100 live" : "V100 offline"}</button>
              <button type="button" className={playing ? "is-active" : ""} disabled={!job || job.frame_count < 2} onClick={() => setPlaying((value) => !value)}>{playing ? "Pause" : "Play"}</button>
              <button type="button" className={followLive ? "is-active" : ""} disabled={!job} onClick={() => { setFollowLive(true); void loadFrame("latest"); }}>Follow live</button>
            </div>
          </div>
          <div className="appac-stage-canvas">
            <WakeScene frame={frame} previewBody={spec.case === "custom-wing" ? previewBody : null} />
            {!frame && spec.case !== "custom-wing" ? (
              <div className="appac-stage-empty">
                <div className="appac-empty-glyph"><span /><span /><span /></div>
                <strong>{connectionError ? "Connect the V100 runner" : "Run the fixture to grow its wake"}</strong>
                <p>{connectionError ? `No response from ${appacStudioBaseUrl()} (${connectionError}).` : "Snapshots will arrive here while APPAC iterates."}</p>
              </div>
            ) : null}
            {!frame && spec.case === "custom-wing" && previewBody ? <span className="appac-preview-badge">DESIGN PREVIEW · HALF-WING SOLVE</span> : null}
            {frame ? (
              <div className="appac-stage-hud">
                <span>ITER <b>{frame.iteration}</b></span>
                <span>RMS <b>{frame.residual == null ? "initial" : frame.residual.toExponential(3)}</b></span>
                <span>FRAME <b>{frame.index + 1}/{frame.count}</b></span>
              </div>
            ) : null}
          </div>
          <div className="appac-timeline">
            <button type="button" disabled={!job || job.frame_count < 2} onClick={() => setPlaying((value) => !value)}>{playing ? "Ⅱ" : "▶"}</button>
            <input aria-label="Wake frame" type="range" min={0} max={Math.max(0, (job?.frame_count ?? 1) - 1)} value={Math.min(frameIndex, Math.max(0, (job?.frame_count ?? 1) - 1))} disabled={!job?.frame_count} onChange={(event) => { setFollowLive(false); setPlaying(false); void loadFrame(Number(event.target.value)); }} />
            <code>{job?.frame_count ? `${frameIndex + 1} / ${job.frame_count}` : "preview"}</code>
          </div>
        </section>

        <aside className="appac-controls">
          <div className="appac-panel-heading"><span>{spec.case === "custom-wing" ? "Wing inspector" : "Fixture"}</span><small>{selectedStation.id}</small></div>
          <div className="appac-control-grid">
            <label><span>Geometry source</span><select value={spec.case} onChange={(event) => { const nextCase = event.target.value as AppacCaseSpec["case"]; setSpec((value) => ({ ...value, case: nextCase, beta: nextCase === "custom-wing" ? 0 : value.beta, alpha: nextCase === "bwb" ? 0 : 4.2 })); setFrame(null); setJob(null); }}><option value="custom-wing">Parametric wing</option><option value="sweptwing">Swept-wing fixture</option><option value="bwb">BWB fixture</option></select></label>
            {spec.case === "custom-wing" ? (
              <>
                <label><span>Wing name</span><input value={wing.name} onChange={(event) => setWing((value) => ({ ...value, name: event.target.value }))} /></label>
                <label><span>Active section</span><select value={selectedStation.id} onChange={(event) => setSelectedStationId(event.target.value)}>{wing.stations.map((station) => <option value={station.id} key={station.id}>{station.id}</option>)}</select></label>
                <div className="appac-two-up">
                  <label><span>Span Y / m</span><input type="number" min={selectedStation.id === wing.stations[0]?.id ? 0 : undefined} step="0.1" value={selectedStation.y} disabled={selectedStation.id === wing.stations[0]?.id} onChange={(event) => changeStation({ y: numberValue(event.target.value, selectedStation.y) })} /></label>
                  <label><span>Chord / m</span><input type="number" min="0.01" step="0.05" value={selectedStation.chord} onChange={(event) => changeStation({ chord: numberValue(event.target.value, selectedStation.chord) })} /></label>
                </div>
                <div className="appac-two-up">
                  <label><span>LE X / m</span><input type="number" step="0.05" value={selectedStation.x_le} onChange={(event) => changeStation({ x_le: numberValue(event.target.value, selectedStation.x_le) })} /></label>
                  <label><span>LE Z / m</span><input type="number" step="0.05" value={selectedStation.z_le} onChange={(event) => changeStation({ z_le: numberValue(event.target.value, selectedStation.z_le) })} /></label>
                </div>
                <div className="appac-two-up">
                  <label><span>Twist / deg</span><input type="number" step="0.25" value={selectedStation.twist_deg} onChange={(event) => changeStation({ twist_deg: numberValue(event.target.value, selectedStation.twist_deg) })} /></label>
                  <label><span>Airfoil</span><input value={selectedStation.airfoil} onChange={(event) => changeStation({ airfoil: event.target.value })} /></label>
                </div>
                <div className="appac-section-actions"><button type="button" disabled={wing.stations.length >= QUICK_LIFTING_LINE_MAX_STATIONS} onClick={addStation}>Add section</button><button type="button" disabled={wing.stations.length <= 2 || selectedStation.id === wing.stations[0]?.id} onClick={deleteStation}>Delete</button></div>
                <div className="appac-two-up">
                  <label><span>Chord panels</span><input type="number" min="4" max="96" value={wing.chord_panels} onChange={(event) => setWing((value) => ({ ...value, chord_panels: Math.round(numberValue(event.target.value, value.chord_panels)) }))} /></label>
                  <label><span>Span / segment</span><input type="number" min="1" max="64" value={wing.span_panels_per_segment} onChange={(event) => setWing((value) => ({ ...value, span_panels_per_segment: Math.round(numberValue(event.target.value, value.span_panels_per_segment)) }))} /></label>
                </div>
                {wingErrors.length ? <p className="appac-validation-error">{wingErrors[0]}</p> : null}
              </>
            ) : <p className="appac-fixture-note">Read-only canonical regression geometry from APPAC {health?.appac_commit.slice(0, 8) ?? "f2a7568"}.</p>}
          </div>

          <div className="appac-control-section appac-solver-section">
            <div className="appac-panel-heading"><span>Solver</span><small>GPU</small></div>
            <div className="appac-two-up">
              <label><span>Alpha / deg</span><input type="number" step="0.2" value={spec.alpha} onChange={(event) => setSpec((value) => ({ ...value, alpha: numberValue(event.target.value, value.alpha) }))} /></label>
              <label><span>Beta / deg</span><input type="number" step="0.2" value={spec.beta} disabled={spec.case === "custom-wing"} title={spec.case === "custom-wing" ? "Half-wing symmetry requires beta = 0" : undefined} onChange={(event) => setSpec((value) => ({ ...value, beta: numberValue(event.target.value, value.beta) }))} /></label>
            </div>
            <div className="appac-two-up">
              <label><span>Linear solve</span><select value={spec.backend} onChange={(event) => setSpec((value) => ({ ...value, backend: event.target.value as AppacCaseSpec["backend"] }))}><option value="lu">LU</option><option value="qr">QR</option></select></label>
              <label><span>Frame stride</span><input type="number" min="1" value={spec.snapshot_interval} onChange={(event) => setSpec((value) => ({ ...value, snapshot_interval: Math.max(1, Math.round(numberValue(event.target.value, value.snapshot_interval))) }))} /></label>
            </div>
            <div className="appac-two-up">
              <label><span>Max iterations</span><input type="number" min="1" value={spec.max_iter} onChange={(event) => setSpec((value) => ({ ...value, max_iter: Math.max(1, Math.round(numberValue(event.target.value, value.max_iter))) }))} /></label>
              <label><span>Convergence</span><input type="number" step="0.000001" value={spec.conv_tol} onChange={(event) => setSpec((value) => ({ ...value, conv_tol: numberValue(event.target.value, value.conv_tol) }))} /></label>
            </div>
            <button type="button" className="appac-run-button" disabled={busy || !health?.cuda.available || (spec.case === "custom-wing" && wingErrors.length > 0)} onClick={() => void run(false)}>{busy ? "Submitting…" : "Run current design on V100"}<span>→</span></button>
            <button type="button" className="appac-force-button" disabled={busy || !health?.cuda.available} onClick={() => void run(true)}>Ignore cached result</button>
          </div>

          <div className="appac-control-section">
            <div className="appac-panel-heading"><span>Sweep</span><small>{spec.case === "custom-wing" ? "α" : "α × β"}</small></div>
            <label><span>Alpha axis</span><input value={alphaAxis} onChange={(event) => setAlphaAxis(event.target.value)} placeholder="0:8:2 or 0, 2, 4" /></label>
            <label><span>Beta axis</span><input value={betaAxis} disabled={spec.case === "custom-wing"} onChange={(event) => setBetaAxis(event.target.value)} placeholder={spec.case === "custom-wing" ? "half-wing: fixed at 0" : "optional"} /></label>
            <button type="button" className="appac-sweep-button" disabled={busy || !health?.cuda.available || (spec.case === "custom-wing" && wingErrors.length > 0)} onClick={() => void runSweep()}>Queue design sweep</button>
          </div>
          {actionError ? <p className="appac-action-error">{actionError}</p> : null}
        </aside>

        <section className="appac-console">
          <div className="appac-console-summary">
            <span className={`appac-state state-${job?.status ?? "idle"}`}>{statusLabel(job)}</span>
            <div><span>iteration</span><b>{job?.latest_iteration ?? 0} / {job?.spec.max_iter ?? spec.max_iter}</b></div>
            <div><span>residual</span><b>{job?.latest_residual == null ? "—" : job.latest_residual.toExponential(3)}</b></div>
            <div><span>cache</span><b>{job?.cache_key ?? "geometry + solver hash"}</b></div>
            <div className="appac-progress"><i style={{ width: `${progress * 100}%` }} /></div>
          </div>
          <div className="appac-log">
            {events.map((event) => (
              <div key={event.seq} className={`level-${event.level}`}>
                <time>{shortTime(event.at)}</time><span>{event.seq.toString().padStart(3, "0")}</span><p>{event.message}</p>{event.residual != null ? <code>{event.residual.toExponential(4)}</code> : null}
              </div>
            ))}
            {!events.length ? <p className="appac-log-empty">V100 output streams here. Geometry and solver settings address the result cache.</p> : null}
          </div>
        </section>
        </div>
      )}
    </main>
  );
}
